import { useState, useEffect, useCallback, useRef } from 'react';
import { repositoryFactory } from '@data';

/**
 * Hook for managing course data with lazy loading
 * Loads course metadata immediately, section details on demand
 */
export function useCourse(courseId) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionsLoaded, setSectionsLoaded] = useState(new Set());
  const sectionsLoadedRef = useRef(new Set());
  const sectionsLoadingRef = useRef(new Set());

  useEffect(() => {
    sectionsLoadedRef.current = new Set();
    sectionsLoadingRef.current = new Set();
    setSectionsLoaded(new Set());
  }, [courseId]);

  useEffect(() => {
    if (!courseId) {
      setLoading(false);
      return;
    }

    const loadCourse = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const courseRepo = repositoryFactory.getCourseRepository();
        const courseData = await courseRepo.getCourseById(courseId);
        
        if (!courseData) {
          throw new Error(`Course not found: ${courseId}`);
        }

        setCourse(courseData);
      } catch (err) {
        console.error('Error loading course:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId]);

  /**
   * Load a specific section's details on demand
   * Prevents loading all sections upfront
   */
  const loadSectionDetails = useCallback(async (sectionId) => {
    if (!courseId || !sectionId) {
      return;
    }

    if (sectionsLoadedRef.current.has(sectionId) || sectionsLoadingRef.current.has(sectionId)) {
      return;
    }

    // In this backend, getCourseById already includes full section details (examples/questions).
    // Avoid redundant section fetches unless section details appear incomplete.
    const existingSection = course?.sections?.find(s => s.id === sectionId);
    const hasInlineDetails = !!existingSection && (
      Array.isArray(existingSection.questions) ||
      Array.isArray(existingSection.examples)
    );

    if (hasInlineDetails) {
      sectionsLoadedRef.current.add(sectionId);
      setSectionsLoaded(prev => new Set([...prev, sectionId]));
      return;
    }

    sectionsLoadingRef.current.add(sectionId);

    try {
      const courseRepo = repositoryFactory.getCourseRepository();
      const section = await courseRepo.getSectionById(courseId, sectionId);

      if (!section) {
        return;
      }

      sectionsLoadedRef.current.add(sectionId);
      setSectionsLoaded(prev => new Set([...prev, sectionId]));

      setCourse(prevCourse => {
        if (!prevCourse?.sections) {
          return prevCourse;
        }

        const hasSection = prevCourse.sections.some(s => s.id === sectionId);
        if (!hasSection) {
          return prevCourse;
        }

        return {
          ...prevCourse,
          sections: prevCourse.sections.map(s => (s.id === sectionId ? section : s)),
        };
      });
    } catch (err) {
      console.error(`Error loading section ${sectionId}:`, err);
    } finally {
      sectionsLoadingRef.current.delete(sectionId);
    }
  }, [courseId, course]);

  return { course, loading, error, loadSectionDetails, sectionsLoaded };
}

/**
 * Hook for getting all courses
 */
export function useCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const courseRepo = repositoryFactory.getCourseRepository();
      const coursesData = await courseRepo.getAllCourses();
      
      setCourses(coursesData);
    } catch (err) {
      console.error('Error loading courses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  /**
   * Create a new course
   */
  const createCourse = async (courseData) => {
    try {
      setError(null);
      const courseRepo = repositoryFactory.getCourseRepository();
      const newCourse = await courseRepo.createCourse(courseData);
      await loadCourses(); // Refresh list
      return newCourse;
    } catch (err) {
      console.error('Error creating course:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Update an existing course
   */
  const updateCourse = async (courseId, courseData) => {
    try {
      setError(null);
      const courseRepo = repositoryFactory.getCourseRepository();
      const updatedCourse = await courseRepo.updateCourse(courseId, courseData);
      await loadCourses(); // Refresh list
      return updatedCourse;
    } catch (err) {
      console.error('Error updating course:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Delete a course
   */
  const deleteCourse = async (courseId) => {
    try {
      setError(null);
      const courseRepo = repositoryFactory.getCourseRepository();
      const success = await courseRepo.deleteCourse(courseId);
      if (success) {
        await loadCourses(); // Refresh list
      }
      return success;
    } catch (err) {
      console.error('Error deleting course:', err);
      setError(err.message);
      throw err;
    }
  };

  return { 
    courses, 
    loading, 
    error, 
    createCourse, 
    updateCourse, 
    deleteCourse,
    refresh: loadCourses 
  };
}

/**
 * Hook for getting section data
 */
export function useSection(courseId, sectionId) {
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!courseId || !sectionId) {
      setLoading(false);
      return;
    }

    const loadSection = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const courseRepo = repositoryFactory.getCourseRepository();
        const sectionData = await courseRepo.getSectionById(courseId, sectionId);
        
        if (!sectionData) {
          throw new Error(`Section not found: ${sectionId}`);
        }

        setSection(sectionData);
      } catch (err) {
        console.error('Error loading section:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadSection();
  }, [courseId, sectionId]);

  return { section, loading, error };
}
